import { NotebookPen } from 'lucide-react'
import { Button } from '@/components/ui/button'
import Card from 'app/frontend/base/components/Card'
import RichTextContent from 'app/frontend/base/components/RichTextContent'
import { Separator } from '@/components/ui/separator'
import { useTeamEventUpdateContext } from '../context/TeamEventUpdateContext'
import NotesEditor, {
  hasNotesContent,
} from 'app/frontend/base/components/NotesEditor'
import { cn } from '@/lib/utils'

type ControlProps = {
  activity: wowaudit.EventActivity
  canEdit: boolean
  isEditing: boolean
  className?: string
  onStartEdit: () => void
}

export const EventActivityNotesControl = ({
  activity,
  canEdit,
  isEditing,
  className,
  onStartEdit,
}: ControlProps) => {
  if (!canEdit || isEditing || hasNotesContent(activity.notes)) return null

  return (
    <button
      type='button'
      onClick={onStartEdit}
      className={cn(
        'group flex items-center gap-2 self-start rounded-md border border-transparent px-2 py-1 text-sm text-muted-foreground transition-colors hover:border-border hover:bg-accent',
        className,
      )}
      aria-label='Add message'
    >
      <NotebookPen className='h-3.5 w-3.5 opacity-60 transition-opacity group-hover:opacity-100' />
      <span>Add message</span>
    </button>
  )
}

type ContentProps = {
  activity: wowaudit.EventActivity
  canEdit: boolean
  isEditing: boolean
  planMessage?: string
  onStartEdit: () => void
  onStopEdit: () => void
}

const EventActivityNotesContent = ({
  activity,
  canEdit,
  isEditing,
  planMessage,
  onStartEdit,
  onStopEdit,
}: ContentProps) => {
  const { updateEventActivity } = useTeamEventUpdateContext()
  const hasActivityNotes = hasNotesContent(activity.notes)
  const hasPlanMessage = hasNotesContent(planMessage)

  if (isEditing) {
    return (
      <div className='flex flex-col gap-1'>
        <span className='text-sm text-muted-foreground'>Message</span>
        <NotesEditor
          initialValue={activity.notes ?? ''}
          onSave={async (notes) => {
            await updateEventActivity(activity.id, { notes })
            onStopEdit()
          }}
          onCancel={onStopEdit}
          clearTarget='this encounter'
        />
      </div>
    )
  }

  if (!hasActivityNotes && !hasPlanMessage) return null

  return (
    <div className='flex flex-col gap-1'>
      <div className='flex items-center justify-between'>
        <span className='text-sm text-muted-foreground'>Message</span>
        {canEdit && hasActivityNotes ? (
          <Button variant='outline' size='xs' onClick={onStartEdit}>
            Edit
          </Button>
        ) : null}
      </div>
      <Card>
        {hasActivityNotes ? (
          <RichTextContent content={activity.notes ?? ''} />
        ) : null}
        {hasActivityNotes && hasPlanMessage ? (
          <Separator className='my-4' />
        ) : null}
        {hasPlanMessage ? (
          <RichTextContent content={planMessage ?? ''} />
        ) : null}
      </Card>
    </div>
  )
}

export default EventActivityNotesContent
